import { useContext, lazy, Suspense, useMemo, useState } from 'react';
import { OfficePageContext, SectionId } from '../Context/OfficePageContext';
import useOfficeEstates from '~js/React/components/CustomHooks/useOfficeEstates';
import useUmbracoOfficeNews from '~js/React/components/CustomHooks/useUmbracoOfficeNews';
import useUmbracoOfficeNewsArchive from '~js/React/components/CustomHooks/useUmbracoOfficeNewsArchive';

const OfficePageHero = lazy(
  () => import('../../components/OfficeComponents/OfficePageNew/OfficePageHero')
);
const OfficeBreadcrumbs = lazy(
  () =>
    import('../../components/OfficeComponents/OfficePageNew/OfficeBreadcrumbs')
);
const OfficeTopMenu = lazy(
  () => import('../../components/OfficeComponents/OfficePageNew/OfficeTopMenu')
);
const OfficeBrokers = lazy(
  () => import('../../components/OfficeComponents/OfficePageNew/OfficeBrokers')
);
const OfficeObjects = lazy(
  () => import('../../components/OfficeComponents/OfficePageNew/OfficeObjects')
);
const OfficeSoldObjects = lazy(
  () =>
    import('../../components/OfficeComponents/OfficePageNew/OfficeSoldObjects')
);
const OfficePartners = lazy(
  () => import('../../components/OfficeComponents/OfficePageNew/OfficePartners')
);
const OfficeLocalAreas = lazy(
  () =>
    import('../../components/OfficeComponents/OfficePageNew/OfficeLocalAreas')
);
const OfficeNews = lazy(
  () => import('../../components/OfficeComponents/OfficePageNew/OfficeNews')
);
const BlockList = lazy(() => import('../../components/Blocks/BlockList'));
const ContactForm = lazy(() => import('~js/React/forms/ContactForm'));

const OfficePageNew = () => {
  const { officeAdminPage, office, archiveId } = useContext(OfficePageContext);
  const globalPartners = officeAdminPage?.properties?.globalPartnerList?.items;
  const officePartners = office?.properties?.partnerList?.items;
  const officeLocalAreas = office?.properties?.localAreas?.items;

  const customerId = office?.properties?.officeId;
  const mirrorId = office?.properties?.mirrorIds;

  const { data: soldOfficeEstateInfo, isLoading: loadingSoldObjects } =
    useOfficeEstates(customerId, false, false, true, mirrorId);

  //this is to fetch news
  const NEWSARCHIVE_ID = archiveId;
  const { data: officeNewsUmbraco } = useUmbracoOfficeNews<any>(office?.id);
  const { data: nationalNewsUmbraco } = useUmbracoOfficeNewsArchive<any>(
    NEWSARCHIVE_ID,
    'Nationellt arkiv' //this needs to be the node name of national news archive
  );

  const { data: countyNewsUmbraco } = useUmbracoOfficeNewsArchive<any>(
    NEWSARCHIVE_ID,
    office?.properties?.officeCounty
      ? `${office?.properties?.officeCounty} arkiv`
      : null
  );

  //sorts and merge county and office news
  const sortedCountyAndOfficeNews = [
    ...(countyNewsUmbraco?.items || []),
    ...(officeNewsUmbraco?.items || []),
  ].sort(
    (a, b) =>
      new Date(b.createDate).getTime() - new Date(a.createDate).getTime()
  );

  //merged all news
  const mergedAllNews = [
    ...(nationalNewsUmbraco?.items || []),
    ...sortedCountyAndOfficeNews,
  ];

  const [newsAmount, setNewsAmount] = useState<number>(3);

  return (
    <>
      <div className="flex flex-col">
        <Suspense>
          <OfficeTopMenu
            loadingSoldObjects={loadingSoldObjects}
            showSoldObjects={soldOfficeEstateInfo?.totalLength > 0}
            newsExist={mergedAllNews?.length > 0}
          />
        </Suspense>

        <div className="m-auto lg:mt-13 w-full max-w-[1680px]">
          <Suspense>
            <OfficePageHero />
          </Suspense>
        </div>
        <div className="mx-auto w-full max-w-container">
          <Suspense>
            <OfficeBreadcrumbs />
          </Suspense>
          {mergedAllNews && mergedAllNews?.length > 0 && (
            <Suspense>
              <OfficeNews news={mergedAllNews} />
            </Suspense>
          )}

          <Suspense>
            <OfficeObjects />
          </Suspense>
        </div>

        <Suspense>
          <OfficeBrokers />
        </Suspense>

        {officeAdminPage?.properties?.blockListCenter?.items && (
          <Suspense>
            <BlockList
              blocks={officeAdminPage?.properties?.blockListCenter?.items}
            />
          </Suspense>
        )}

        {officeAdminPage?.properties?.showFormOffice && (
          <Suspense>
            <ContactForm officeAdminPage={officeAdminPage} office={office} />
          </Suspense>
        )}

        <div className="mx-auto w-full max-w-container">
          {soldOfficeEstateInfo && soldOfficeEstateInfo?.totalLength > 0 && (
            <Suspense>
              <OfficeSoldObjects
                officeEstateInfo={soldOfficeEstateInfo}
                isLoading={loadingSoldObjects}
              />
            </Suspense>
          )}
          {(globalPartners || officePartners) && (
            <Suspense>
              <OfficePartners />
            </Suspense>
          )}
        </div>
        {officeAdminPage?.properties?.blockListBottom?.items && (
          <Suspense>
            <BlockList
              blocks={officeAdminPage?.properties?.blockListBottom?.items}
            />
          </Suspense>
        )}
        {officeLocalAreas && officeLocalAreas?.length > 0 && (
          <div className="mx-auto w-full max-w-container">
            <Suspense>
              <OfficeLocalAreas />
            </Suspense>
          </div>
        )}
      </div>
    </>
  );
};

export default OfficePageNew;
